// src/components/ReminderProvider/ReminderProvider.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ReminderModal from '../ReminderModal/ReminderModal';
import { useReminders } from '../../hooks/useReminders';

const SKIP_ROUTES = [
  '/', '/signup', '/signup-2', '/signup-3', '/reset', '/reminders',
];

const isSkipRoute = (pathname) => {
  return SKIP_ROUTES.some(route => {
    if (route === '/') return pathname === '/';
    return pathname === route || pathname.startsWith(route + '/');
  });
};

const ReminderProvider = ({ children }) => {
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);

  const loginCheckDoneRef = useRef(false);
  const previousPathRef = useRef(location.pathname);

  const {
    reminders,
    isAdmin,
    adminReady,
    checkOnLogin,
    removeReminder,
    resetSession,
  } = useReminders();

  // ⭐ EFFECT 0: Detect login page visit → reset session
  // Jab bhi user "/" (login page) pe jaye, sessionStorage clear karo
  useEffect(() => {
    if (location.pathname === '/') {
      resetSession();           // ⭐ sessionStorage clear
      loginCheckDoneRef.current = false;
      setShowModal(false);
    }
  }, [location.pathname, resetSession]);

  // ⭐ EFFECT 1: On login — show modal ONCE per session
  useEffect(() => {
    if (!adminReady || !isAdmin) return;
    if (loginCheckDoneRef.current) return;
    if (isSkipRoute(location.pathname)) return;

    const runLoginCheck = async () => {
      loginCheckDoneRef.current = true;
      const result = await checkOnLogin();

      if (isSkipRoute(window.location.pathname)) return;

      if (result.hasReminders && result.count > 0 && !result.alreadyShown) {
        setShowModal(true);
      }
    };

    runLoginCheck();
  }, [adminReady, isAdmin, checkOnLogin, location.pathname]);

  // ⭐ EFFECT 3: Force close on skip routes
  useEffect(() => {
    if (isSkipRoute(location.pathname)) {
      setShowModal(false);
    }
  }, [location.pathname]);

  const handleAcknowledge = (reminderId) => {
    removeReminder(reminderId);

    if (reminderId === 'all' || reminders.length <= 1) {
      setShowModal(false);
    }

    window.dispatchEvent(new Event('remindersUpdated'));
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const shouldRenderModal = isAdmin && !isSkipRoute(location.pathname);

  return (
    <>
      {children}
      {shouldRenderModal && (
        <ReminderModal
          isOpen={showModal}
          onClose={handleClose}
          reminders={reminders}
          onAcknowledge={handleAcknowledge}
        />
      )}
    </>
  );
};

export default ReminderProvider;